import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaTrophy, FaUsers, FaChartLine, FaFutbol, FaMedal, FaCalendarAlt, FaMapMarkerAlt, FaClock, FaChevronLeft, FaChevronRight, FaHome, FaRoad, FaPercentage, FaBullseye, FaShieldAlt, FaFire, FaEye, FaHandPaper, FaRunning, FaStopwatch, FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import PlayerCard from '../components/Equipe/PlayerCard';
import TopBar from '../components/Layout/TopBar';
import { fetchClassement, fetchMatchs, fetchClassementEvolution, fetchEquipesChampionnat, fetchEquipeDetails, fetchClassementEquipe, fetchEngagementsEquipe, fetchStatsEquipeChampionnat } from '../api/fff';
import TopJoueurCard from '../components/Equipe/TopJoueurCard';
import ClassementSparkline from '../components/Equipe/ClassementSparkline';
import CardBloc from '../components/Equipe/CardBloc';
import EquipeResultats from '../components/Equipe/EquipeResultats';
import EquipeStaff from '../components/Equipe/EquipeStaff';
import useInView from '../hooks/useInView';

const postes = [
  { key: 'Gardien', label: 'Gardiens' },
  { key: 'Défenseur', label: 'Défenseurs' },
  { key: 'Milieu', label: 'Milieux' },
  { key: 'Attaquant', label: 'Attaquants' },
];

const EquipeDetail = () => {
  const { slug } = useParams();
  const [equipe, setEquipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('data');
  const [joueurs, setJoueurs] = useState([]);
  const [loadingJoueurs, setLoadingJoueurs] = useState(true);
  const [stats, setStats] = useState(null); // stats Firestore
  const [statsApi, setStatsApi] = useState(null); // stats API FFF
  const [loadingStats, setLoadingStats] = useState(false);
  const [classementEvolution, setClassementEvolution] = useState([]);
  const [classementPoule, setClassementPoule] = useState([]);
  const [loadingClassement, setLoadingClassement] = useState(false);
  const [equipeFffId, setEquipeFffId] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [classementFff, setClassementFff] = useState([]);
  const [loadingClassementFff, setLoadingClassementFff] = useState(false);
  const [detailsDebug, setDetailsDebug] = useState(null);
  const [loadingEngagements, setLoadingEngagements] = useState(false);

  // Refs pour animations
  const [tabsRef, tabsInView] = useInView({ threshold: 0.2 });
  const [contentRef, contentInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    const fetchEquipe = async () => {
      setLoading(true);
      const q = query(collection(db, 'equipes'), where('slug', '==', slug));
      const snap = await getDocs(q);
      if (!snap.empty) {
        setEquipe({ id: snap.docs[0].id, ...snap.docs[0].data() });
      }
      setLoading(false);
    };
    fetchEquipe();
  }, [slug]);

  // Charger les joueurs (pour effectif ET top buteurs/passeurs)
  useEffect(() => {
    if (!equipe?.id) return;
    const fetchJoueurs = async () => {
      setLoadingJoueurs(true);
      const q = query(collection(db, 'joueurs'), where('equipe', '==', equipe.id), where('visible', '==', true));
      const snap = await getDocs(q);
      setJoueurs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoadingJoueurs(false);
    };
    fetchJoueurs();
  }, [equipe]);

  // Charger les stats Firestore (fallback)
  useEffect(() => {
    if (!equipe?.id) return;
    const fetchStats = async () => {
      const q = query(collection(db, 'stats_equipes'), where('equipe', '==', equipe.id));
      const snap = await getDocs(q);
      if (!snap.empty) {
        setStats({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } else {
        setStats(null);
      }
    };
    fetchStats();
  }, [equipe]);

  // Charger les stats API FFF dynamiquement pour le championnat
  useEffect(() => {
    if (!equipe || tab !== 'data') return;
    // On cherche l'engagement de championnat (CH)
    const engagementCH = engagements.find(e => e.competition.type === 'CH');
    if (!engagementCH) return;
    const cpNo = engagementCH.competition.cp_no;
    const phNo = engagementCH.phase?.number;
    const gpNo = engagementCH.poule?.stage_number;
    if (cpNo && phNo && gpNo) {
      fetchStatsEquipeChampionnat({ cpNo, phNo, gpNo, nomEquipe: 'FEIGNIES/AULNOYE EFC' }).then(stats => {
        if (stats) setStatsApi(stats);
      }).catch(error => {
        console.error('Erreur lors de la récupération des statistiques:', error);
      });
    }
  }, [equipe, tab, engagements]);

  // Récupérer l'évolution du classement dynamiquement
  useEffect(() => {
    if (!equipe) return;
    let isMounted = true;
    const getEvolution = async () => {
      try {
        const evo = await fetchClassementEvolution(equipe.nom);
        if (isMounted) setClassementEvolution(evo);
      } catch (e) {
        if (isMounted) setClassementEvolution([]);
      }
    };
    getEvolution();
    return () => { isMounted = false; };
  }, [equipe]);

  // Charger le classement de la poule pour l'onglet classement
  useEffect(() => {
    if (tab !== 'classement') return;
    // On ne charge le classement que pour l'équipe N2
    if (equipe && (equipe.slug === 'n2' || equipe.nom.toLowerCase().includes('n2'))) {
      setLoadingClassement(true);
      fetchClassement().then(data => {
        setClassementPoule(data);
        setLoadingClassement(false);
      });
    } else {
      setClassementPoule([]);
    }
  }, [tab, equipe]);

  
  // Charger le classement de la compétition sélectionnée
  useEffect(() => {
    const getClassement = async () => {
      if (!selectedEngagement || !selectedEngagement.competition || selectedEngagement.competition.type !== 'CH') {
        setClassementFff([]);
        return;
      }
      setLoadingClassementFff(true);
      const cpNo = selectedEngagement.competition.cp_no;
      const phNo = selectedEngagement.phase?.number;
      const gpNo = selectedEngagement.poule?.stage_number;
      console.log('fetchClassementEquipe params:', { cpNo, phNo, gpNo, selectedEngagement });
      if (cpNo && phNo && gpNo) {
        const classement = await fetchClassementEquipe({ cpNo, phNo, gpNo });
        setClassementFff(classement);
      } else {
        setClassementFff([]);
      }
      setLoadingClassementFff(false);
    };
    getClassement();
  }, [selectedEngagement]);

  // Charger les engagements de l'équipe
  useEffect(() => {
    if (!equipe?.fffId) return;
    
    const loadEngagements = async () => {
      setLoadingEngagements(true);
      try {
        const engagementsData = await fetchEngagementsEquipe(equipe.fffId);
        setEngagements(engagementsData);
        // Sélectionner le premier engagement de type CH (championnat), sinon le premier CP (coupe)
        let defaultEngagement = engagementsData.find(e => e.competition.type === 'CH');
        if (!defaultEngagement) {
          defaultEngagement = engagementsData.find(e => e.competition.type === 'CP');
        }
        if (defaultEngagement) {
          setSelectedEngagement(defaultEngagement);
        } else {
          setSelectedEngagement(null);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des engagements:", error);
      }
      setLoadingEngagements(false);
    };

    loadEngagements();
  }, [equipe?.fffId]);

  // Top buteurs/passeurs dynamiques
  const topButeurs = [...joueurs].sort((a, b) => (b.buts || 0) - (a.buts || 0)).slice(0, 3);
  const topPasseurs = [...joueurs].sort((a, b) => (b.passes || 0) - (a.passes || 0)).slice(0, 3);

  // Helper pour fallback Firestore -> API -> vide
  const getStat = (key) => {
    if (stats && stats[key] !== undefined && stats[key] !== null && stats[key] !== '') return stats[key];
    if (statsApi && statsApi[key] !== undefined && statsApi[key] !== null) return statsApi[key];
    return '-';
  };

  // Slider horizontal simple (scrollable + flèches)
  const sliderRefs = useRef({});
  const scroll = (posteKey, dir) => {
    const ref = sliderRefs.current[posteKey];
    if (ref) {
      const cardWidth = 240; // Largeur approximative d'une carte + gap
      ref.scrollBy({ left: dir * cardWidth * 2, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002766] to-[#001B56] flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FFE400] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-2xl font-title font-bold text-white mb-2">Chargement de l'équipe...</h2>
          <p className="text-white/70 font-accent">Récupération des données EFAFC</p>
        </div>
      </div>
    );
  }
  
  if (!equipe) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002766] to-[#001B56] flex justify-center items-center">
        <div className="text-center">
          <div className="text-6xl mb-6">⚽</div>
          <h2 className="text-2xl font-title font-bold text-white mb-4">Équipe introuvable</h2>
          <Link to="/equipes" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl font-accent font-bold hover:shadow-lg transition-all duration-300">
            <FaArrowLeft />
            Retour aux équipes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002766] to-[#001B56]">
      {/* Effets de fond */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.05, 0.15, 0.05]
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#FFE400] rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.05, 0.1, 0.05]
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
          className="absolute bottom-1/3 left-1/3 w-80 h-80 bg-[#BDFF00] rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10">
        <TopBar />
        
        {/* Bouton retour modernisé */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute top-24 left-6 z-50"
        >
          <Link 
            to="/equipes" 
            className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white hover:bg-white/20 hover:text-[#FFE400] transition-all duration-300 font-accent font-bold shadow-lg"
          >
            <FaArrowLeft className="text-lg" />
            Retour aux équipes
          </Link>
        </motion.div>

        {/* Header visuel conservé */}
        <div className="relative flex items-center justify-center min-h-[60vh] md:min-h-[70vh] w-full max-w-6xl mx-auto mt-8">
          {/* Image de fond */}
          <div className="absolute inset-0 rounded-[3rem] overflow-hidden shadow-2xl w-full h-full flex items-center justify-center">
            <img
              src={equipe.photo}
              alt={equipe.nom}
              className="w-full h-full object-cover object-center"
              style={{ minHeight: '60vh', maxHeight: '80vh' }}
            />
            {/* Overlay dégradé bleu foncé bas -> haut */}
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(0deg, #001B56 10%, rgba(0,27,86,0.0) 100%)',
              opacity: 0.92
            }} />
          </div>
          {/* Contenu texte centré */}
          <div className="absolute bottom-0 left-0 w-full h-full flex flex-col items-center justify-end pb-16 px-4 z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white drop-shadow-lg uppercase text-center mb-2">{equipe.nom}</h1>
            <span className="text-xl md:text-2xl font-bold text-[#FFE400] tracking-wide mb-4 text-center">{equipe.slogan}</span>
            <p className="text-white/90 max-w-2xl text-center mb-6 text-lg md:text-xl">{equipe.description}</p>
          </div>
        </div>

        {/* Onglets modernisés */}
        <motion.div
          ref={tabsRef}
          initial={{ opacity: 0, y: 30 }}
          animate={tabsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="flex justify-center mt-12 mb-8 px-4"
        >
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-2 border border-white/20 shadow-2xl">
            <div className="flex flex-wrap justify-center gap-2">
              {[
                { key: 'data', label: 'DATA', icon: FaChartLine },
                { key: 'effectif', label: 'EFFECTIF', icon: FaUsers },
                { key: 'staff', label: 'STAFF', icon: FaMedal },
                { key: 'classement', label: 'CLASSEMENT', icon: FaTrophy },
                { key: 'resultats', label: 'RÉSULTATS', icon: FaFutbol }
              ].map((tabItem) => (
                <motion.button
                  key={tabItem.key}
                  onClick={() => setTab(tabItem.key)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-4 py-3 rounded-2xl font-accent font-bold text-sm transition-all duration-300 ${
                    tab === tabItem.key
                      ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] shadow-lg'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <tabItem.icon className="text-lg" />
                  <span className="hidden sm:inline">{tabItem.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Contenu des onglets */}
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, y: 30 }}
          animate={contentInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="container mx-auto px-4 pb-16 max-w-[1600px]"
        >
          <AnimatePresence mode="wait">
            {tab === 'data' && (
              <motion.div
                key="data"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                {!statsApi ? (
                  <div className="flex justify-center items-center py-16">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-[#FFE400] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-white/80 font-accent">Chargement des statistiques...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Section principale - Classement et bilan */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Classement */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl hover:bg-white/15 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                            <FaTrophy className="text-2xl text-[#001B56]" />
                          </div>
                          <div>
                            <h3 className="text-white font-title font-bold text-lg">Classement</h3>
                            <p className="text-white/60 text-sm font-accent">Position actuelle</p>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className="text-5xl font-extrabold text-[#FFE400] block">{statsApi.rank || '-'}<sup className="text-2xl">e</sup></span>
                          <span className="text-white font-accent font-bold mt-2 block text-sm">{engagements.find(e => e.competition.type === 'CH')?.competition?.name || '-'}</span>
                          <span className="text-white/60 text-xs mt-1 block">Saison 2023/2024</span>
                        </div>
                      </motion.div>

                      {/* Points */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.15 }}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl hover:bg-white/15 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                            <FaChartLine className="text-2xl text-[#001B56]" />
                          </div>
                          <div>
                            <h3 className="text-white font-title font-bold text-lg">Points</h3>
                            <p className="text-white/60 text-sm font-accent">Total saison</p>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className="text-5xl font-extrabold text-[#BDFF00] block">{statsApi.point_count || '0'}</span>
                          <div className="mt-2 text-xs text-white/60">
                            {statsApi.total_games_count ? `${((statsApi.point_count || 0) / ((statsApi.total_games_count || 1) * 3) * 100).toFixed(1)}% du maximum` : 'Aucun match'}
                          </div>
                        </div>
                      </motion.div>

                      {/* Matchs joués */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl hover:bg-white/15 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                            <FaFutbol className="text-2xl text-[#001B56]" />
                          </div>
                          <div>
                            <h3 className="text-white font-title font-bold text-lg">Matchs</h3>
                            <p className="text-white/60 text-sm font-accent">Joués cette saison</p>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className="text-5xl font-extrabold text-white block">{statsApi.total_games_count || '0'}</span>
                          <div className="mt-2 text-xs text-white/60">
                            Sur ~26 matchs de championnat
                          </div>
                        </div>
                      </motion.div>

                      {/* Différentiel de buts */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.25 }}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl hover:bg-white/15 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                            <FaChartLine className="text-2xl text-[#001B56]" />
                          </div>
                          <div>
                            <h3 className="text-white font-title font-bold text-lg">Goal Average</h3>
                            <p className="text-white/60 text-sm font-accent">Différentiel</p>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className={`text-5xl font-extrabold block ${(statsApi.goals_diff || 0) >= 0 ? 'text-[#BDFF00]' : 'text-red-400'}`}>
                            {(statsApi.goals_diff || 0) >= 0 ? '+' : ''}{statsApi.goals_diff || '0'}
                          </span>
                          <div className="mt-2 text-xs text-white/60">
                            {(statsApi.goals_for_count || 0)} pour - {(statsApi.goals_against_count || 0)} contre
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Section bilan détaillé */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Bilan V/N/D avec pourcentages */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl hover:bg-white/15 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                            <FaUsers className="text-2xl text-[#001B56]" />
                          </div>
                          <div>
                            <h3 className="text-white font-title font-bold text-lg">Bilan Détaillé</h3>
                            <p className="text-white/60 text-sm font-accent">Victoires / Nuls / Défaites</p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          {/* Victoires */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FaChevronLeft className="text-[#BDFF00] text-xl" />
                              <span className="text-white font-accent">Victoires</span>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-extrabold text-[#BDFF00]">{statsApi.won_games_count || 0}</span>
                              <div className="text-xs text-white/60">
                                {statsApi.total_games_count ? `${((statsApi.won_games_count || 0) / (statsApi.total_games_count || 1) * 100).toFixed(1)}%` : '0%'}
                              </div>
                            </div>
                          </div>
                          {/* Nuls */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FaChevronRight className="text-yellow-400 text-xl" />
                              <span className="text-white font-accent">Nuls</span>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-extrabold text-yellow-400">{statsApi.draw_games_count || 0}</span>
                              <div className="text-xs text-white/60">
                                {statsApi.total_games_count ? `${((statsApi.draw_games_count || 0) / (statsApi.total_games_count || 1) * 100).toFixed(1)}%` : '0%'}
                              </div>
                            </div>
                          </div>
                          {/* Défaites */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FaArrowLeft className="text-red-400 text-xl" />
                              <span className="text-white font-accent">Défaites</span>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-extrabold text-red-400">{statsApi.lost_games_count || 0}</span>
                              <div className="text-xs text-white/60">
                                {statsApi.total_games_count ? `${((statsApi.lost_games_count || 0) / (statsApi.total_games_count || 1) * 100).toFixed(1)}%` : '0%'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      {/* Statistiques offensives/défensives */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.35 }}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl hover:bg-white/15 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                            <FaFutbol className="text-2xl text-[#001B56]" />
                          </div>
                          <div>
                            <h3 className="text-white font-title font-bold text-lg">Attaque vs Défense</h3>
                            <p className="text-white/60 text-sm font-accent">Moyennes par match</p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          {/* Attaque */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FaFutbol className="text-[#BDFF00] text-xl" />
                              <span className="text-white font-accent">Buts marqués</span>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-extrabold text-[#BDFF00]">{statsApi.goals_for_count || 0}</span>
                              <div className="text-xs text-white/60">
                                {statsApi.total_games_count ? `${((statsApi.goals_for_count || 0) / (statsApi.total_games_count || 1)).toFixed(1)} / match` : '0 / match'}
                              </div>
                            </div>
                          </div>
                          {/* Défense */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FaMedal className="text-red-400 text-xl" />
                              <span className="text-white font-accent">Buts encaissés</span>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-extrabold text-red-400">{statsApi.goals_against_count || 0}</span>
                              <div className="text-xs text-white/60">
                                {statsApi.total_games_count ? `${((statsApi.goals_against_count || 0) / (statsApi.total_games_count || 1)).toFixed(1)} / match` : '0 / match'}
                              </div>
                            </div>
                          </div>
                          {/* Clean sheets */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FaTrophy className="text-blue-400 text-xl" />
                              <span className="text-white font-accent">Clean sheets</span>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-extrabold text-blue-400">
                                {statsApi.total_games_count ? Math.max(0, (statsApi.total_games_count || 0) - (statsApi.goals_against_count || 0)) : 0}
                              </span>
                              <div className="text-xs text-white/60">
                                Matchs sans encaisser
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Section top joueurs de l'équipe */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 }}
                      className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl"
                    >
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                          <FaUsers className="text-2xl text-[#001B56]" />
                        </div>
                        <div>
                          <h3 className="text-white font-title font-bold text-lg">Top Joueurs de l'Équipe</h3>
                          <p className="text-white/60 text-sm font-accent">Meilleurs buteurs et passeurs</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Top buteurs */}
                        <div>
                          <h4 className="text-[#BDFF00] font-accent font-bold mb-4 flex items-center gap-2">
                            <FaFutbol className="text-lg" />
                            Meilleurs Buteurs
                          </h4>
                          <div className="space-y-3">
                            {topButeurs.slice(0, 3).map((joueur, index) => (
                              <div key={joueur.id} className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                                <div className="flex items-center gap-3">
                                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    index === 0 ? 'bg-yellow-500 text-black' : 
                                    index === 1 ? 'bg-gray-400 text-black' : 
                                    'bg-orange-600 text-white'
                                  }`}>
                                    {index + 1}
                                  </span>
                                  <div>
                                    <span className="text-white font-accent font-bold block">{joueur.prenom} {joueur.nom}</span>
                                    <span className="text-white/60 text-xs">{joueur.poste}</span>
                                  </div>
                                </div>
                                <span className="text-[#BDFF00] font-bold text-lg">{joueur.buts || 0}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Top passeurs */}
                        <div>
                          <h4 className="text-[#FFE400] font-accent font-bold mb-4 flex items-center gap-2">
                            <FaMedal className="text-lg" />
                            Meilleurs Passeurs
                          </h4>
                          <div className="space-y-3">
                            {topPasseurs.slice(0, 3).map((joueur, index) => (
                              <div key={joueur.id} className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                                <div className="flex items-center gap-3">
                                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    index === 0 ? 'bg-yellow-500 text-black' : 
                                    index === 1 ? 'bg-gray-400 text-black' : 
                                    'bg-orange-600 text-white'
                                  }`}>
                                    {index + 1}
                                  </span>
                                  <div>
                                    <span className="text-white font-accent font-bold block">{joueur.prenom} {joueur.nom}</span>
                                    <span className="text-white/60 text-xs">{joueur.poste}</span>
                                  </div>
                                </div>
                                <span className="text-[#FFE400] font-bold text-lg">{joueur.passes || 0}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Section métriques avancées */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.65 }}
                      className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl"
                    >
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                          <FaChartLine className="text-2xl text-[#001B56]" />
                        </div>
                        <div>
                          <h3 className="text-white font-title font-bold text-lg">Métriques Avancées</h3>
                          <p className="text-white/60 text-sm font-accent">Analyses détaillées de performance</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Efficacité offensive */}
                        <div className="text-center">
                          <div className="bg-white/5 rounded-2xl p-4">
                            <FaFutbol className="text-[#BDFF00] text-2xl mx-auto mb-2" />
                            <span className="text-2xl font-extrabold text-[#BDFF00] block">
                              {statsApi.total_games_count ? ((statsApi.goals_for_count || 0) / (statsApi.total_games_count || 1)).toFixed(1) : '0.0'}
                            </span>
                            <span className="text-white/80 text-sm font-accent">Buts/Match</span>
                          </div>
                        </div>

                        {/* Solidité défensive */}
                        <div className="text-center">
                          <div className="bg-white/5 rounded-2xl p-4">
                            <FaMedal className="text-blue-400 text-2xl mx-auto mb-2" />
                            <span className="text-2xl font-extrabold text-blue-400 block">
                              {statsApi.total_games_count ? ((statsApi.goals_against_count || 0) / (statsApi.total_games_count || 1)).toFixed(1) : '0.0'}
                            </span>
                            <span className="text-white/80 text-sm font-accent">Buts encaissés/Match</span>
                          </div>
                        </div>

                        {/* Taux de victoire */}
                        <div className="text-center">
                          <div className="bg-white/5 rounded-2xl p-4">
                            <FaTrophy className="text-[#FFE400] text-2xl mx-auto mb-2" />
                            <span className="text-2xl font-extrabold text-[#FFE400] block">
                              {statsApi.total_games_count ? (((statsApi.won_games_count || 0) / (statsApi.total_games_count || 1)) * 100).toFixed(0) : '0'}%
                            </span>
                            <span className="text-white/80 text-sm font-accent">Taux de victoire</span>
                          </div>
                        </div>

                        {/* Points par match */}
                        <div className="text-center">
                          <div className="bg-white/5 rounded-2xl p-4">
                            <FaChartLine className="text-white text-2xl mx-auto mb-2" />
                            <span className="text-2xl font-extrabold text-white block">
                              {statsApi.total_games_count ? ((statsApi.point_count || 0) / (statsApi.total_games_count || 1)).toFixed(1) : '0.0'}
                            </span>
                            <span className="text-white/80 text-sm font-accent">Points/Match</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Section comparaison avec la moyenne du championnat */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.7 }}
                      className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl"
                    >
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                          <FaChartLine className="text-2xl text-[#001B56]" />
                        </div>
                        <div>
                          <h3 className="text-white font-title font-bold text-lg">Comparaison Championnat</h3>
                          <p className="text-white/60 text-sm font-accent">Performance vs moyenne du championnat</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Attaque */}
                        <div className="text-center">
                          <h4 className="text-[#BDFF00] font-accent font-bold mb-4">Attaque</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-white/80 text-sm">Notre équipe</span>
                              <span className="text-[#BDFF00] font-bold">
                                {statsApi.total_games_count ? ((statsApi.goals_for_count || 0) / (statsApi.total_games_count || 1)).toFixed(1) : '0.0'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-white/80 text-sm">Moyenne championnat</span>
                              <span className="text-white/60 font-bold">1.4</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-[#BDFF00] to-[#FFE400] h-2 rounded-full transition-all duration-1000"
                                style={{ 
                                  width: `${Math.min(100, ((statsApi.goals_for_count || 0) / (statsApi.total_games_count || 1)) / 1.4 * 100)}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Défense */}
                        <div className="text-center">
                          <h4 className="text-blue-400 font-accent font-bold mb-4">Défense</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-white/80 text-sm">Notre équipe</span>
                              <span className="text-blue-400 font-bold">
                                {statsApi.total_games_count ? ((statsApi.goals_against_count || 0) / (statsApi.total_games_count || 1)).toFixed(1) : '0.0'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-white/80 text-sm">Moyenne championnat</span>
                              <span className="text-white/60 font-bold">1.3</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-1000"
                                style={{ 
                                  width: `${Math.min(100, (1.3 - ((statsApi.goals_against_count || 0) / (statsApi.total_games_count || 1))) / 1.3 * 100)}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Points */}
                        <div className="text-center">
                          <h4 className="text-[#FFE400] font-accent font-bold mb-4">Points</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-white/80 text-sm">Notre équipe</span>
                              <span className="text-[#FFE400] font-bold">
                                {statsApi.total_games_count ? ((statsApi.point_count || 0) / (statsApi.total_games_count || 1)).toFixed(1) : '0.0'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-white/80 text-sm">Moyenne championnat</span>
                              <span className="text-white/60 font-bold">1.5</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] h-2 rounded-full transition-all duration-1000"
                                style={{ 
                                  width: `${Math.min(100, ((statsApi.point_count || 0) / (statsApi.total_games_count || 1)) / 1.5 * 100)}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
              </motion.div>
            )}
            {tab === 'effectif' && (
              <motion.div
                key="effectif"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                {postes.map((poste, posteIndex) => {
                  const joueursPoste = joueurs.filter(j => j.poste === poste.key);
                  if (joueursPoste.length === 0) return null;
                  
                  return (
                    <motion.div
                      key={poste.key}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: posteIndex * 0.1 }}
                      className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                          <FaUsers className="text-2xl text-[#001B56]" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-title font-bold text-white">{poste.label}</h3>
                          <p className="text-white/60 font-accent">{joueursPoste.length} joueur{joueursPoste.length > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      
                      <div className="relative">
                        {/* Flèche gauche modernisée */}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => scroll(poste.key, -1)}
                          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full p-3 shadow-lg hover:bg-white/20 transition-all duration-300 hidden md:flex items-center justify-center"
                          style={{marginLeft: -50}}
                          aria-label="Précédent"
                        >
                          <FaChevronLeft className="text-lg" />
                        </motion.button>
                        
                        {/* Slider horizontal */}
                        <div
                          ref={el => (sliderRefs.current[poste.key] = el)}
                          className="flex gap-6 overflow-x-auto scrollbar-hide px-12 py-4"
                          style={{scrollBehavior: 'smooth'}}
                        >
                          {joueursPoste.map((joueur, index) => (
                            <motion.div
                              key={joueur.id}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: posteIndex * 0.1 + index * 0.05 }}
                              className="flex-shrink-0"
                            >
                              <PlayerCard joueur={joueur} />
                            </motion.div>
                          ))}
                        </div>
                        
                        {/* Flèche droite modernisée */}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => scroll(poste.key, 1)}
                          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full p-3 shadow-lg hover:bg-white/20 transition-all duration-300 hidden md:flex items-center justify-center"
                          style={{marginRight: -50}}
                          aria-label="Suivant"
                        >
                          <FaChevronRight className="text-lg" />
                        </motion.button>
                      </div>
                    </motion.div>
                  );
                })}
                
                {/* Si aucun joueur */}
                {joueurs.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                  >
                    <div className="bg-white/10 backdrop-blur-md rounded-3xl p-12 border border-white/20 max-w-md mx-auto">
                      <div className="text-6xl mb-6">👥</div>
                      <h3 className="text-2xl font-title font-bold text-white mb-4">Aucun joueur</h3>
                      <p className="text-white/70 font-accent">
                        Aucun joueur à afficher pour cette équipe.
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
            {tab === 'staff' && (
              <motion.div
                key="staff"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
                className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10"
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-4 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                    <FaMedal className="text-3xl text-[#001B56]" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-title font-bold text-white">Staff Technique</h2>
                    <p className="text-white/60 font-accent">Encadrement de l'équipe</p>
                  </div>
                </div>
                <EquipeStaff clubId={186901} equipeNom={equipe.nom} equipeId={equipe.id} />
              </motion.div>
            )}
            {tab === 'classement' && (
              <motion.div
                key="classement"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-4 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                    <FaTrophy className="text-3xl text-[#001B56]" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-title font-bold text-white">Classements</h2>
                    <p className="text-white/60 font-accent">Position dans les compétitions</p>
                  </div>
                </div>

                {/* Sélecteur de compétition */}
                {engagements.filter(e => e.competition.type === 'CH' || e.competition.type === 'CP').length > 0 && (
                  <div className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10">
                    <h3 className="text-white font-title font-bold text-lg mb-4">Sélectionner une compétition</h3>
                    <div className="flex flex-wrap gap-3">
                      {engagements.filter(e => e.competition.type === 'CH' || e.competition.type === 'CP').map((engagement) => (
                        <motion.button
                          key={engagement.id}
                          onClick={() => setSelectedEngagement(engagement)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-6 py-3 rounded-2xl font-accent font-bold text-sm transition-all duration-300 ${
                            selectedEngagement?.id === engagement.id
                              ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] shadow-lg'
                              : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                          }`}
                        >
                          {engagement.competition.name}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loader */}
                {loadingClassementFff && (
                  <div className="bg-white/5 backdrop-blur-md rounded-3xl p-16 border border-white/10 text-center">
                    <div className="w-12 h-12 border-4 border-[#FFE400] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-white/80 font-accent">Chargement du classement...</p>
                  </div>
                )}

                {/* Affichage du classement */}
                {!loadingClassementFff && selectedEngagement && selectedEngagement.competition.type === 'CH' && classementFff.length > 0 && (
                  <div className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10 overflow-hidden">
                    <h3 className="text-white font-title font-bold text-xl mb-6">
                      Classement - {selectedEngagement.competition.name}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-[#FFE400] text-left border-b border-white/20">
                            <th className="py-3 px-4 font-accent font-bold">Rang</th>
                            <th className="py-3 px-4 font-accent font-bold">Équipe</th>
                            <th className="py-3 px-4 font-accent font-bold">Pts</th>
                            <th className="py-3 px-4 font-accent font-bold">J</th>
                            <th className="py-3 px-4 font-accent font-bold">V</th>
                            <th className="py-3 px-4 font-accent font-bold">N</th>
                            <th className="py-3 px-4 font-accent font-bold">D</th>
                            <th className="py-3 px-4 font-accent font-bold">Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classementFff.map((team, idx) => {
                            const isClub = (team.equipe?.short_name_federation || team.equipe?.short_name || team.equipe?.nom || '').toLowerCase().includes('feignies/aulnoye');
                            return (
                              <motion.tr
                                key={idx}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                className={
                                  isClub
                                    ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] font-bold'
                                    : 'border-b border-white/10 hover:bg-white/5 transition-colors text-white'
                                }
                              >
                                <td className="py-3 px-4 font-accent">{team.rank}</td>
                                <td className="py-3 px-4 font-accent">{team.equipe?.short_name_federation || team.equipe?.short_name || team.equipe?.nom || '—'}</td>
                                <td className="py-3 px-4 font-accent font-bold">{team.point_count}</td>
                                <td className="py-3 px-4 font-accent">{team.total_games_count}</td>
                                <td className="py-3 px-4 font-accent">{team.won_games_count}</td>
                                <td className="py-3 px-4 font-accent">{team.draw_games_count}</td>
                                <td className="py-3 px-4 font-accent">{team.lost_games_count}</td>
                                <td className="py-3 px-4 font-accent">{team.goals_diff}</td>
                              </motion.tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Cas coupe (CP) */}
                {!loadingClassementFff && selectedEngagement && selectedEngagement.competition.type === 'CP' && (
                  <div className="bg-white/5 backdrop-blur-md rounded-3xl p-12 border border-white/10 text-center">
                    <div className="p-4 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl inline-block mb-6">
                      <FaMedal className="text-3xl text-[#001B56]" />
                    </div>
                    <h3 className="text-[#FFE400] font-title font-bold text-2xl mb-4">Parcours en coupe</h3>
                    {selectedEngagement.en_tour_no && selectedEngagement.en_tour_no > 0 ? (
                      <div className="space-y-2">
                        <span className="text-white text-xl block">Tour atteint :</span>
                        <span className="font-bold text-[#FFE400] text-3xl">{selectedEngagement.en_tour_no}</span>
                      </div>
                    ) : (
                      <span className="text-white/70 font-accent">Aucun parcours renseigné</span>
                    )}
                  </div>
                )}

                {/* Messages d'état */}
                {!loadingClassementFff && selectedEngagement && classementFff.length === 0 && selectedEngagement.competition.type === 'CH' && (
                  <div className="bg-white/5 backdrop-blur-md rounded-3xl p-12 border border-white/10 text-center">
                    <div className="text-6xl mb-6">📊</div>
                    <h3 className="text-white font-title font-bold text-xl mb-4">Aucun classement</h3>
                    <p className="text-white/70 font-accent">Aucun classement disponible pour cette compétition.</p>
                  </div>
                )}

                {!loadingClassementFff && (!selectedEngagement || (engagements.filter(e => e.competition.type === 'CH' || e.competition.type === 'CP').length === 0)) && (
                  <div className="bg-white/5 backdrop-blur-md rounded-3xl p-12 border border-white/10 text-center">
                    <div className="text-6xl mb-6">🏆</div>
                    <h3 className="text-white font-title font-bold text-xl mb-4">Aucune compétition</h3>
                    <p className="text-white/70 font-accent">Aucune compétition avec classement à afficher pour cette équipe.</p>
                  </div>
                )}
              </motion.div>
            )}
            {tab === 'resultats' && (
              <motion.div
                key="resultats"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
                className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10"
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-4 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl">
                    <FaFutbol className="text-3xl text-[#001B56]" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-title font-bold text-white">Résultats</h2>
                    <p className="text-white/60 font-accent">Historique des matchs</p>
                  </div>
                </div>
                <EquipeResultats clubId={186901} equipeId={equipe.fffId} nomEquipe={equipe.nom} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default EquipeDetail;